import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	List,
	ListItemButton,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	Add,
	ArrowBack,
	ArrowDownward,
	ArrowUpward,
	CheckCircle,
	Close,
	ContentCopy,
	CreateNewFolder,
	DeleteOutline,
	Error as ErrorIcon,
	PlaylistAddCheck,
	OpenInNew,
	PlayArrow,
	PlaylistPlay,
	Save,
	Visibility,
	VisibilityOff,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import testService from "../services/testService";
import executionService from "../services/executionService";
import { fetchProjects } from "../store/slices/projectSlice";
import { createFolder, fetchFolders } from "../store/slices/folderSlice";
import OwnerBadge from "../components/OwnerBadge";
import CloneDialog from "../components/CloneDialog";
import StepCheckDetails from "../components/StepCheckDetails";
import ChecksFromResponseDialog from "../components/ChecksFromResponseDialog";
import { canModify, isViewOnly } from "../utils/permissions";
import { newStepId, withStepIds } from "../utils/stepUtils";
import { responseJson } from "../utils/apiChecks";

// Which requests running request `index` on its own needs: it, plus for each
// {{variable}} it uses the latest turned-on request before it that saves that
// variable (and, in turn, what those use). Indexes, in order.
const VARIABLE = /\{\{\s*(\w+)\s*}}/g;
const requestsNeededFor = (steps, index) => {
	const needed = new Set([index]);
	const pending = [index];
	while (pending.length) {
		const i = pending.pop();
		const text = JSON.stringify({ request: steps[i].request, apiAssertions: steps[i].apiAssertions });
		for (const [, name] of text.matchAll(VARIABLE)) {
			for (let j = i - 1; j >= 0; j--) {
				if (!steps[j].disabled && (steps[j].extract || []).some((e) => e.variable === name)) {
					if (!needed.has(j)) {
						needed.add(j);
						pending.push(j);
					}
					break;
				}
			}
		}
	}
	return [...needed].sort((a, b) => a - b);
};

// API tests (Test.testType "API"): a list of HTTP requests run in order without a
// browser. Each request can check the response and save values as {{variables}} for
// the requests after it. Saved like any test, so suites, schedules and CI runs work.

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];
const METHOD_COLORS = { GET: "success", POST: "warning", PUT: "info", PATCH: "secondary", DELETE: "error" };
const SOURCES = [
	{ value: "status", label: "Status code" },
	{ value: "responseTime", label: "Response time (ms)" },
	{ value: "jsonPath", label: "JSON value" },
	{ value: "header", label: "Header" },
	{ value: "body", label: "Body text" },
];
const OPERATORS = [
	{ value: "equals", label: "equals" },
	{ value: "notEquals", label: "does not equal" },
	{ value: "contains", label: "contains" },
	{ value: "notContains", label: "does not contain" },
	{ value: "lessThan", label: "is less than" },
	{ value: "greaterThan", label: "is greater than" },
	{ value: "exists", label: "exists" },
	{ value: "notExists", label: "does not exist" },
	{ value: "matches", label: "matches (regex)" },
];
const TERMINAL = ["SUCCESS", "FAILED", "CANCELLED"];
const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" };

const newRequest = () => ({
	id: newStepId(),
	type: "apiRequest",
	text: "",
	request: { method: "GET", url: "", headers: [], query: [], bodyType: "none", body: "", form: [] },
	apiAssertions: [{ source: "status", operator: "equals", expected: "200" }],
	extract: [],
});

const EMPTY_TEST = { name: "", description: "", projectId: "", folderId: "", tags: [], steps: [], testType: "API", status: "DRAFT" };

const requestTitle = (step) => step.text || step.request?.url || "Untitled request";

/** Rows of key / value (headers, query parameters, form fields). */
function KeyValueEditor({ rows = [], onChange, readOnly, keyLabel = "Name", valueLabel = "Value" }) {
	const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
	return (
		<Box>
			{rows.map((row, i) => (
				<Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
					<Tooltip title={row.disabled ? "Off: not sent" : "On"}>
						<span>
							<Checkbox size="small" checked={!row.disabled} disabled={readOnly} onChange={(e) => update(i, { disabled: !e.target.checked || undefined })} />
						</span>
					</Tooltip>
					<TextField size="small" label={keyLabel} value={row.key || ""} disabled={readOnly} onChange={(e) => update(i, { key: e.target.value })} sx={{ flex: 1 }} inputProps={{ style: mono }} />
					<TextField size="small" label={valueLabel} value={row.value || ""} disabled={readOnly} onChange={(e) => update(i, { value: e.target.value })} sx={{ flex: 2 }} inputProps={{ style: mono }} />
					{!readOnly && (
						<IconButton size="small" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove">
							<Close fontSize="small" />
						</IconButton>
					)}
				</Box>
			))}
			{!readOnly && (
				<Button size="small" startIcon={<Add />} onClick={() => onChange([...rows, { key: "", value: "" }])}>
					Add
				</Button>
			)}
		</Box>
	);
}

function ChecksEditor({ checks = [], onChange, readOnly, lastResponse }) {
	const update = (i, patch) => onChange(checks.map((c, j) => (j === i ? { ...c, ...patch } : c)));
	const [fromResponseOpen, setFromResponseOpen] = useState(false);
	// Keyed on the body text: polling a run replaces the objects, which would reset the dialog's ticks
	const body = lastResponse ? lastResponse.body ?? "" : null;
	const parsed = useMemo(() => responseJson(body === null ? null : { body }), [body]);
	return (
		<Box>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
				The request fails when any check fails. With no checks, it fails on a 4xx or 5xx status.
			</Typography>
			{checks.map((c, i) => {
				const needsPath = c.source === "jsonPath" || c.source === "header";
				const needsValue = !["exists", "notExists"].includes(c.operator);
				return (
					<Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: { xs: "wrap", md: "nowrap" } }}>
						<FormControl size="small" sx={{ minWidth: 170 }}>
							<InputLabel>Check</InputLabel>
							<Select label="Check" value={c.source} disabled={readOnly} onChange={(e) => update(i, { source: e.target.value })}>
								{SOURCES.map((s) => (
									<MenuItem key={s.value} value={s.value}>
										{s.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{needsPath && (
							<TextField
								size="small"
								label={c.source === "header" ? "Header name" : "JSON path"}
								placeholder={c.source === "header" ? "Content-Type" : "$.data.id"}
								value={c.path || ""}
								disabled={readOnly}
								onChange={(e) => update(i, { path: e.target.value })}
								sx={{ flex: 1, minWidth: 140 }}
								inputProps={{ style: mono }}
							/>
						)}
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Comparison</InputLabel>
							<Select label="Comparison" value={c.operator} disabled={readOnly} onChange={(e) => update(i, { operator: e.target.value })}>
								{OPERATORS.map((o) => (
									<MenuItem key={o.value} value={o.value}>
										{o.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{needsValue && (
							<TextField size="small" label="Expected" value={c.expected ?? ""} disabled={readOnly} onChange={(e) => update(i, { expected: e.target.value })} sx={{ flex: 1, minWidth: 120 }} inputProps={{ style: mono }} />
						)}
						{!readOnly && (
							<IconButton size="small" onClick={() => onChange(checks.filter((_, j) => j !== i))} aria-label="Remove check">
								<Close fontSize="small" />
							</IconButton>
						)}
					</Box>
				);
			})}
			{!readOnly && (
				<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
					<Button size="small" startIcon={<Add />} onClick={() => onChange([...checks, { source: "jsonPath", path: "", operator: "equals", expected: "" }])}>
						Add check
					</Button>
					<Tooltip title={parsed.error || "Pick values from this request's last response to check"}>
						<span>
							<Button size="small" startIcon={<PlaylistAddCheck />} disabled={!!parsed.error} onClick={() => setFromResponseOpen(true)}>
								From last response
							</Button>
						</span>
					</Tooltip>
				</Box>
			)}
			<ChecksFromResponseDialog
				open={fromResponseOpen}
				json={parsed.json}
				existingChecks={checks}
				onClose={() => setFromResponseOpen(false)}
				onAdd={(added) => {
					onChange([...checks, ...added]);
					setFromResponseOpen(false);
				}}
			/>
		</Box>
	);
}

function ExtractEditor({ rows = [], onChange, readOnly }) {
	const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
	return (
		<Box>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
				Save values from the response and use them in later requests as <Box component="code" sx={mono}>{"{{name}}"}</Box> (in the URL,
				parameters, headers, body and checks). Handy for ids and login tokens.
			</Typography>
			{rows.map((r, i) => (
				<Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
					<TextField size="small" label="Variable" placeholder="userId" value={r.variable || ""} disabled={readOnly} onChange={(e) => update(i, { variable: e.target.value.replace(/\W/g, "") })} sx={{ width: 160 }} inputProps={{ style: mono }} />
					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel>From</InputLabel>
						<Select label="From" value={r.source} disabled={readOnly} onChange={(e) => update(i, { source: e.target.value })}>
							<MenuItem value="jsonPath">JSON value</MenuItem>
							<MenuItem value="header">Header</MenuItem>
							<MenuItem value="status">Status code</MenuItem>
							<MenuItem value="body">Body text</MenuItem>
						</Select>
					</FormControl>
					{(r.source === "jsonPath" || r.source === "header") && (
						<TextField size="small" label={r.source === "header" ? "Header name" : "JSON path"} placeholder={r.source === "header" ? "Location" : "$.id"} value={r.path || ""} disabled={readOnly} onChange={(e) => update(i, { path: e.target.value })} sx={{ flex: 1 }} inputProps={{ style: mono }} />
					)}
					{!readOnly && (
						<IconButton size="small" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove">
							<Close fontSize="small" />
						</IconButton>
					)}
				</Box>
			))}
			{!readOnly && (
				<Button size="small" startIcon={<Add />} onClick={() => onChange([...rows, { variable: "", source: "jsonPath", path: "" }])}>
					Save a value
				</Button>
			)}
		</Box>
	);
}

function RequestEditor({ step, onChange, readOnly, variables, lastResponse }) {
	const [tab, setTab] = useState("params");
	const req = step.request || {};
	const setReq = (patch) => onChange({ ...step, request: { ...req, ...patch } });
	const jsonError = useMemo(() => {
		if (req.bodyType !== "json" || !req.body?.trim()) return null;
		// {{variables}} are filled in at run time; check the rest
		try {
			JSON.parse(req.body.replace(/\{\{\s*\w+\s*}}/g, "0"));
			return null;
		} catch (e) {
			return e.message;
		}
	}, [req.bodyType, req.body]);
	const count = (list) => (list || []).filter((r) => r.key && !r.disabled).length;

	return (
		<Box>
			<TextField fullWidth size="small" label="Name (optional)" value={step.text || ""} disabled={readOnly} onChange={(e) => onChange({ ...step, text: e.target.value })} placeholder="e.g. Create a user" sx={{ mb: 2 }} />
			<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
				<Select size="small" value={req.method || "GET"} disabled={readOnly} onChange={(e) => setReq({ method: e.target.value })} sx={{ width: 120, ...mono, fontWeight: 600 }}>
					{METHODS.map((m) => (
						<MenuItem key={m} value={m} sx={mono}>
							{m}
						</MenuItem>
					))}
				</Select>
				<TextField fullWidth size="small" placeholder="https://api.example.com/users/{{userId}}" value={req.url || ""} disabled={readOnly} onChange={(e) => setReq({ url: e.target.value })} inputProps={{ style: mono }} />
			</Box>
			{variables.length > 0 && (
				<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
					<Typography variant="caption" color="text.secondary">
						Available here:
					</Typography>
					{variables.map((v) => (
						<Chip key={v} size="small" variant="outlined" label={`{{${v}}}`} sx={mono} />
					))}
				</Box>
			)}
			<Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
				<Tab value="params" label={`Parameters${count(req.query) ? ` (${count(req.query)})` : ""}`} />
				<Tab value="headers" label={`Headers${count(req.headers) ? ` (${count(req.headers)})` : ""}`} />
				<Tab value="body" label={`Body${req.bodyType && req.bodyType !== "none" ? ` (${req.bodyType})` : ""}`} />
				<Tab value="checks" label={`Checks (${(step.apiAssertions || []).length})`} />
				<Tab value="save" label={`Save values${(step.extract || []).length ? ` (${step.extract.length})` : ""}`} />
				<Tab value="settings" label="Settings" />
			</Tabs>

			{tab === "params" && <KeyValueEditor rows={req.query} readOnly={readOnly} onChange={(query) => setReq({ query })} />}
			{tab === "headers" && (
				<KeyValueEditor rows={req.headers} readOnly={readOnly} onChange={(headers) => setReq({ headers })} keyLabel="Header" />
			)}
			{tab === "body" && (
				<Box>
					<RadioGroup row value={req.bodyType || "none"} onChange={(e) => setReq({ bodyType: e.target.value })}>
						{["none", "json", "text", "form"].map((t) => (
							<FormControlLabel key={t} value={t} disabled={readOnly} control={<Radio size="small" />} label={{ none: "None", json: "JSON", text: "Text", form: "Form" }[t]} />
						))}
					</RadioGroup>
					{(req.bodyType === "json" || req.bodyType === "text") && (
						<>
							<TextField
								fullWidth
								multiline
								minRows={8}
								value={req.body || ""}
								disabled={readOnly}
								onChange={(e) => setReq({ body: e.target.value })}
								placeholder={req.bodyType === "json" ? '{\n  "name": "Jane",\n  "email": "{{email}}"\n}' : ""}
								error={!!jsonError}
								helperText={jsonError ? `Not valid JSON: ${jsonError}` : req.bodyType === "json" ? "Sent with Content-Type: application/json unless you set one" : " "}
								inputProps={{ style: { ...mono, fontSize: 13 } }}
							/>
							{req.bodyType === "json" && !readOnly && !jsonError && req.body?.trim() && !/\{\{/.test(req.body) && (
								<Button size="small" onClick={() => setReq({ body: JSON.stringify(JSON.parse(req.body), null, 2) })}>
									Format
								</Button>
							)}
						</>
					)}
					{req.bodyType === "form" && <KeyValueEditor rows={req.form} readOnly={readOnly} onChange={(form) => setReq({ form })} keyLabel="Field" />}
				</Box>
			)}
			{tab === "checks" && <ChecksEditor checks={step.apiAssertions} readOnly={readOnly} lastResponse={lastResponse} onChange={(apiAssertions) => onChange({ ...step, apiAssertions })} />}
			{tab === "save" && <ExtractEditor rows={step.extract} readOnly={readOnly} onChange={(extract) => onChange({ ...step, extract })} />}
			{tab === "settings" && (
				<Box sx={{ maxWidth: 360 }}>
					<TextField
						fullWidth
						size="small"
						type="number"
						label="Timeout (ms)"
						value={req.timeoutMs ?? ""}
						placeholder="30000"
						disabled={readOnly}
						onChange={(e) => setReq({ timeoutMs: e.target.value === "" ? undefined : Number(e.target.value) })}
						inputProps={{ min: 1, max: 300000 }}
					/>
					<FormControlLabel
						sx={{ mt: 1 }}
						control={<Checkbox checked={!step.disabled} disabled={readOnly} onChange={(e) => onChange({ ...step, disabled: e.target.checked ? undefined : true })} />}
						label="Run this request (off: skipped)"
					/>
				</Box>
			)}
		</Box>
	);
}

export default function ApiTestEditor() {
	const { id } = useParams();
	const isNew = !id || id === "new";
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const { enqueueSnackbar } = useSnackbar();
	const { projects } = useSelector((state) => state.projects);
	const { folders } = useSelector((state) => state.folders);
	const [folderDialogOpen, setFolderDialogOpen] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");
	const [creatingFolder, setCreatingFolder] = useState(false);

	const [test, setTest] = useState(() => ({
		...EMPTY_TEST,
		projectId: searchParams.get("project") || "",
		folderId: searchParams.get("folder") || "",
		steps: [newRequest()],
	}));
	const [selected, setSelected] = useState(0);
	const [loading, setLoading] = useState(!isNew);
	const [saving, setSaving] = useState(false);
	const [dirty, setDirty] = useState(false);
	const [cloneOpen, setCloneOpen] = useState(false);
	const [run, setRun] = useState(null); // the last run started here
	const [runScope, setRunScope] = useState(null); // {index, count} when it ran some requests
	const pollRef = useRef(null);
	const readOnly = !isNew && isViewOnly(test);

	useEffect(() => {
		dispatch(fetchProjects());
	}, [dispatch]);

	useEffect(() => {
		if (isNew) return;
		(async () => {
			try {
				const data = await testService.getTest(id);
				if (data.testType !== "API") {
					navigate(`/tests/${id}`, { replace: true });
					return;
				}
				setTest({ ...EMPTY_TEST, ...data, steps: withStepIds(data.steps || []) });
				setSelected(0);
			} catch (err) {
				enqueueSnackbar(err.response?.data?.message || "Failed to load the test", { variant: "error" });
				navigate("/apis");
			} finally {
				setLoading(false);
			}
		})();
	}, [id, isNew, navigate, enqueueSnackbar]);

	useEffect(() => () => clearInterval(pollRef.current), []);

	// The project's API folders (its test folders are for browser tests)
	useEffect(() => {
		if (test.projectId) dispatch(fetchFolders(test.projectId));
	}, [test.projectId, dispatch]);
	const apiFolders = (folders || []).filter((f) => f.projectId === test.projectId && f.kind === "API");

	const addFolder = async () => {
		if (!newFolderName.trim()) return;
		setCreatingFolder(true);
		try {
			const folder = await dispatch(
				createFolder({ name: newFolderName.trim(), projectId: test.projectId, parentFolderId: null, kind: "API" }),
			).unwrap();
			change({ folderId: folder.id });
			setFolderDialogOpen(false);
			setNewFolderName("");
			enqueueSnackbar("Folder created", { variant: "success" });
		} catch (err) {
			enqueueSnackbar(err.message || "Couldn't create the folder", { variant: "error" });
		} finally {
			setCreatingFolder(false);
		}
	};

	const change = (patch) => {
		setTest((t) => ({ ...t, ...patch }));
		setDirty(true);
	};
	const setSteps = (steps) => change({ steps });
	const updateStep = (i, step) => setSteps(test.steps.map((s, j) => (j === i ? step : s)));
	const moveStep = (i, delta) => {
		const steps = [...test.steps];
		[steps[i], steps[i + delta]] = [steps[i + delta], steps[i]];
		setSteps(steps);
		setSelected(i + delta);
	};

	// {{variables}} earlier requests save, for the selected request
	const variablesBefore = (index) =>
		[...new Set(test.steps.slice(0, index).flatMap((s) => (s.disabled ? [] : (s.extract || []).map((e) => e.variable).filter(Boolean))))];

	const save = async () => {
		if (!test.name.trim()) {
			enqueueSnackbar("Please enter a test name", { variant: "warning" });
			return null;
		}
		const missing = test.steps.findIndex((s) => !s.request?.url?.trim());
		if (missing >= 0) {
			setSelected(missing);
			enqueueSnackbar(`Request ${missing + 1} needs a URL`, { variant: "warning" });
			return null;
		}
		setSaving(true);
		try {
			const saved = isNew ? await testService.createTest(test) : await testService.updateTest(id, test);
			setDirty(false);
			enqueueSnackbar(isNew ? "API test created" : "API test saved", { variant: "success" });
			if (isNew) navigate(`/api-tests/${saved.id}`, { replace: true });
			else setTest({ ...EMPTY_TEST, ...saved, steps: withStepIds(saved.steps || []) });
			return saved;
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Failed to save the test", { variant: "error" });
			return null;
		} finally {
			setSaving(false);
		}
	};

	const poll = (executionId) => {
		clearInterval(pollRef.current);
		const tick = async () => {
			try {
				const execution = await executionService.getExecution(executionId);
				setRun(execution);
				if (TERMINAL.includes(execution.status)) clearInterval(pollRef.current);
			} catch {
				clearInterval(pollRef.current);
			}
		};
		tick();
		pollRef.current = setInterval(tick, 1500);
	};

	// Runs the whole test, or with indexes only those requests (of request
	// `target`); saves first if there are changes
	const runTest = async (indexes, target) => {
		let testId = id;
		let steps = test.steps;
		if (!readOnly && (dirty || isNew)) {
			const saved = await save();
			if (!saved) return;
			testId = saved.id;
			steps = saved.steps || steps;
		}
		try {
			setRun({ status: "QUEUED", stepResults: [] });
			setRunScope(indexes ? { index: target, count: indexes.length } : null);
			if (indexes) setSelected(target);
			const started = await testService.executeTest(testId, indexes?.map((i) => steps[i].id));
			poll(started.executionId);
		} catch (err) {
			setRun(null);
			enqueueSnackbar(err.response?.data?.message || "Couldn't start the run", { variant: "error" });
		}
	};

	const resultFor = (index) => run?.stepResults?.find((r) => String(r.stepId) === String(index));
	const running = run && !TERMINAL.includes(run.status);
	const step = test.steps[selected];
	const selectedResult = resultFor(selected);

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
				<IconButton onClick={() => navigate(test.projectId ? `/apis?project=${test.projectId}` : "/apis")} aria-label="Back to APIs">
					<ArrowBack />
				</IconButton>
				<Box sx={{ flexGrow: 1 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography variant="h4" fontWeight={700}>
							{isNew ? "New API Test" : readOnly ? "View API Test" : "Edit API Test"}
						</Typography>
						<Chip label="API" color="info" size="small" />
					</Box>
					<Typography variant="body2" color="text.secondary">
						HTTP requests run in order, without a browser. Add it to suites, schedules and CI runs like any test.
					</Typography>
					<OwnerBadge item={test} sx={{ mt: 1 }} />
				</Box>
				{!isNew && (
					<Button variant="outlined" startIcon={<ContentCopy />} onClick={() => setCloneOpen(true)}>
						Clone
					</Button>
				)}
				<Button variant="outlined" startIcon={running ? <CircularProgress size={16} /> : <PlayArrow />} onClick={() => runTest()} disabled={running || saving}>
					{running ? "Running…" : !readOnly && (dirty || isNew) ? "Save & run" : "Run"}
				</Button>
				{!readOnly && (
					<Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>
						{saving ? "Saving…" : "Save"}
					</Button>
				)}
			</Box>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Grid container spacing={2}>
						<Grid item xs={12} md={5}>
							<TextField fullWidth required size="small" label="Test name" value={test.name} disabled={readOnly} onChange={(e) => change({ name: e.target.value })} placeholder="e.g. Users API" />
						</Grid>
						<Grid item xs={12} md={7}>
							<TextField fullWidth size="small" label="Description" value={test.description || ""} disabled={readOnly} onChange={(e) => change({ description: e.target.value })} />
						</Grid>
						<Grid item xs={12} md={5}>
							<FormControl fullWidth size="small">
								<InputLabel>Project</InputLabel>
								<Select label="Project" value={test.projectId || ""} disabled={readOnly} onChange={(e) => change({ projectId: e.target.value, folderId: "" })}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{(projects || [])
										.filter((p) => canModify(p) || p.id === test.projectId)
										.map((p) => (
											<MenuItem key={p.id} value={p.id}>
												{p.name}
											</MenuItem>
										))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} md={7}>
							<Box sx={{ display: "flex", gap: 1 }}>
								<FormControl fullWidth size="small" disabled={readOnly || !test.projectId}>
									<InputLabel>Folder</InputLabel>
									<Select label="Folder" value={test.folderId || ""} onChange={(e) => change({ folderId: e.target.value })}>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{apiFolders.map((f) => (
											<MenuItem key={f.id} value={f.id}>
												{f.path || f.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								{!readOnly && (
									<Tooltip title={test.projectId ? "New API folder" : "Choose a project first"}>
										<span>
											<IconButton onClick={() => setFolderDialogOpen(true)} disabled={!test.projectId} aria-label="New API folder">
												<CreateNewFolder />
											</IconButton>
										</span>
									</Tooltip>
								)}
							</Box>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			<Dialog open={folderDialogOpen} onClose={() => setFolderDialogOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>New API folder</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						label="Folder name"
						value={newFolderName}
						onChange={(e) => setNewFolderName(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && addFolder()}
						sx={{ mt: 1 }}
						helperText="Groups API tests in this project. Test folders are separate."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={addFolder} disabled={creatingFolder || !newFolderName.trim()}>
						{creatingFolder ? "Creating…" : "Create"}
					</Button>
				</DialogActions>
			</Dialog>

			{run && (
				<Alert
					severity={run.status === "SUCCESS" ? "success" : run.status === "FAILED" ? "error" : run.status === "CANCELLED" ? "warning" : "info"}
					sx={{ mb: 2 }}
					action={
						run.id && (
							<Button color="inherit" size="small" endIcon={<OpenInNew fontSize="small" />} onClick={() => navigate(`/executions?executionId=${run.id}`)}>
								Open run
							</Button>
						)
					}
				>
					{running
						? runScope
							? `Running request ${runScope.index + 1}${runScope.count > 1 ? ` and the ${runScope.count - 1} before it that it needs` : ""}…`
							: `Running… ${run.stepResults?.length || 0} of ${test.steps.length} requests done`
						: run.status === "SUCCESS"
							? runScope
								? `Request ${runScope.index + 1} passed.`
								: "Every request passed."
							: run.status === "FAILED"
								? run.errorMessage || "A request failed."
								: "The run was stopped."}{" "}
					Select a request to see its response.
				</Alert>
			)}

			<Grid container spacing={3}>
				<Grid item xs={12} md={4}>
					<Card>
						<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, mb: 1 }}>
								<Typography variant="subtitle1" fontWeight={600}>
									Requests ({test.steps.length})
								</Typography>
								{!readOnly && (
									<Button
										size="small"
										startIcon={<Add />}
										onClick={() => {
											setSteps([...test.steps, newRequest()]);
											setSelected(test.steps.length);
										}}
									>
										Add
									</Button>
								)}
							</Box>
							{test.steps.length === 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
									No requests yet.
								</Typography>
							)}
							<List dense disablePadding>
								{test.steps.map((s, i) => {
									const result = resultFor(i);
									return (
										<ListItemButton key={s.id} selected={i === selected} onClick={() => setSelected(i)} sx={{ borderRadius: 1, gap: 1, opacity: s.disabled ? 0.55 : 1 }}>
											<Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
												{i + 1}
											</Typography>
											<Chip size="small" label={s.request?.method || "GET"} color={METHOD_COLORS[s.request?.method || "GET"] || "default"} variant="outlined" sx={{ ...mono, fontWeight: 700, minWidth: 64 }} />
											<Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, ...(s.text ? {} : mono) }}>
												{requestTitle(s)}
											</Typography>
											{result?.status === "SUCCESS" && <CheckCircle fontSize="small" color="success" />}
											{result?.status === "FAILED" && <ErrorIcon fontSize="small" color="error" />}
											{s.disabled && <VisibilityOff fontSize="small" color="disabled" />}
										</ListItemButton>
									);
								})}
							</List>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} md={8}>
					{step ? (
						<Card>
							<CardContent>
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
									<Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
										Request {selected + 1}
									</Typography>
									<Tooltip
										title={
											requestsNeededFor(test.steps, selected).length > 1
												? `Also runs request ${requestsNeededFor(test.steps, selected)
														.slice(0, -1)
														.map((i) => i + 1)
														.join(", ")}: this one uses values saved there${dirty || isNew ? ". Saves your changes first" : ""}`
												: `Runs just this request${dirty || isNew ? ". Saves your changes first" : ""}`
										}
									>
										<span>
											<Button size="small" startIcon={<PlayArrow />} disabled={running || saving} onClick={() => runTest(requestsNeededFor(test.steps, selected), selected)}>
												Run request
											</Button>
										</span>
									</Tooltip>
									{selected > 0 && (
										<Tooltip title={`Run requests 1–${selected + 1} (turned-off ones are skipped), e.g. when an earlier request logs in with a cookie`}>
											<span>
												<IconButton
													size="small"
													aria-label="Run up to this request"
													disabled={running || saving}
													onClick={() => runTest(test.steps.map((_, i) => i).filter((i) => i === selected || (i < selected && !test.steps[i].disabled)), selected)}
												>
													<PlaylistPlay fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									)}
									{!readOnly && (
										<Box sx={{ ml: 1 }}>
											<Tooltip title="Move up">
												<span>
													<IconButton size="small" disabled={selected === 0} onClick={() => moveStep(selected, -1)}>
														<ArrowUpward fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
											<Tooltip title="Move down">
												<span>
													<IconButton size="small" disabled={selected === test.steps.length - 1} onClick={() => moveStep(selected, 1)}>
														<ArrowDownward fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
											<Tooltip title={step.disabled ? "Turn on" : "Turn off (skip when running)"}>
												<IconButton size="small" onClick={() => updateStep(selected, { ...step, disabled: step.disabled ? undefined : true })}>
													{step.disabled ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
												</IconButton>
											</Tooltip>
											<Tooltip title="Duplicate">
												<IconButton
													size="small"
													onClick={() => {
														const steps = [...test.steps];
														steps.splice(selected + 1, 0, { ...structuredClone(step), id: newStepId() });
														setSteps(steps);
														setSelected(selected + 1);
													}}
												>
													<ContentCopy fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete">
												<IconButton
													size="small"
													onClick={() => {
														setSteps(test.steps.filter((_, j) => j !== selected));
														setSelected(Math.max(0, selected - 1));
													}}
												>
													<DeleteOutline fontSize="small" />
												</IconButton>
											</Tooltip>
										</Box>
									)}
								</Box>
								<RequestEditor
									key={step.id}
									step={step}
									readOnly={readOnly}
									onChange={(s) => updateStep(selected, s)}
									variables={variablesBefore(selected)}
									lastResponse={run?.stepResults?.find((r) => r.metadata?.stepUid === step.id)?.metadata?.api?.response}
								/>
								{selectedResult && (
									<Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
										<Typography variant="subtitle2" gutterBottom>
											Last run {selectedResult.status === "SUCCESS" ? "✓ passed" : selectedResult.status === "FAILED" ? "✗ failed" : "(skipped)"}
										</Typography>
										{selectedResult.errorMessage && (
											<Alert severity="error" sx={{ mb: 1 }}>
												{selectedResult.errorMessage}
											</Alert>
										)}
										<StepCheckDetails stepResult={selectedResult} executionId={run?.id} stepIndex={selected} />
									</Box>
								)}
							</CardContent>
						</Card>
					) : (
						<Card>
							<CardContent>
								<Typography color="text.secondary">Add a request to get started.</Typography>
							</CardContent>
						</Card>
					)}
				</Grid>
			</Grid>

			<CloneDialog open={cloneOpen} kind="test" item={test} onClose={() => setCloneOpen(false)} onCloned={(clone) => navigate(`/api-tests/${clone.id}`)} />
		</Box>
	);
}
